import { gql, useApolloClient } from "@apollo/client";
import { Button, Flex, Portal, Text } from "@chakra-ui/react";
import React from "react";
import { RiAddFill, RiDownloadLine } from "react-icons/ri";
import { WithRequiredHasuraRoles } from "src/components/Permissions/WithRequiredHasuraRoles";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { triggerDownload } from "src/services/dataTransfer";
import * as FormTemplate from "src/services/formTemplate";
import * as Export from "src/services/formTemplate/export";
import * as GQL from "src/types/graphql";
import { HasuraRole } from "src/types/hasuraRole";
import { ModeMenu } from "../../components/ModeMenu";
import { useFormTemplateContext } from "../../formTemplateContext";
import { GET_FORM_TEMPLATE_CONTENT_BY_ID } from "../../graphql/queries";
import { useFormBuilderV1Feature } from "../../hooks/useFormBuilderV1Feature";
import { AddSectionButton } from "./components/AddSectionButton";
import { useEditFormTemplateContext } from "./context";
import { FormTemplateSection } from "./FormTemplateSection";
import { ALL_SECTION_TYPES } from "./services";

type Props = {
  organizationId: uuid;
  gradesConfig: GQL.GetGradesConfigByOrganization_grade_config[];
};
export const Content: React.FC<Props> = ({ organizationId, gradesConfig }) => {
  const { state, formTemplateId, formTemplateVersion } =
    useEditFormTemplateContext();
  const { menuRef } = useFormTemplateContext();
  const sortedSections = state?.get("sortedSections");

  const { isFormBuilderFullEdit } = useFormBuilderV1Feature();
  const flags = useFlags(["form-builder-draft-mode"]);

  const exportFormTemplate = FSQ.useExportFormTemplate(
    {
      path: {
        formTemplateId: formTemplateId ?? "",
      },
    },
    undefined,
    {
      enabled: false,
    }
  );

  const avelaToast = useAvelaToast();
  const [exporting, setExporting] = React.useState(false);
  const apolloClient = useApolloClient();
  const exportFormTemplateJSON = async () => {
    if (formTemplateId === undefined) {
      return;
    }

    setExporting(true);
    if (formTemplateVersion === "2.0.0") {
      // v2.0.0, use form service to export form template
      const data = await exportFormTemplate.refetch();
      if (!data.data?.data) {
        avelaToast.genericError();
        return;
      }

      triggerDownload(
        new Blob([JSON.stringify(data.data.data, undefined, 2)], {
          type: "text/json",
        }),
        "formTemplate.json"
      );

      setExporting(false);
      return;
    }

    // v1.0.0, use frontend code to export form template
    const [data, exportContextData] = await Promise.all([
      apolloClient.query<
        GQL.GetFormTemplateContentById,
        GQL.GetFormTemplateContentByIdVariables
      >({
        query: GET_FORM_TEMPLATE_CONTENT_BY_ID,
        variables: { form_template_id: formTemplateId },
        fetchPolicy: "no-cache",
      }),
      apolloClient.query<GQL.GetExportContext, GQL.GetExportContextVariables>({
        query: gql`
          query GetExportContext($formTemplateId: uuid!) {
            grade_config(
              where: {
                grades: {
                  enrollment_period: {
                    form_templates: { id: { _eq: $formTemplateId } }
                  }
                }
              }
            ) {
              id
              label
              value
            }

            school(
              where: {
                grades: {
                  enrollment_period: {
                    form_templates: { id: { _eq: $formTemplateId } }
                  }
                }
              }
            ) {
              id
              reference_id
              name
            }
          }
        `,
        variables: { formTemplateId },
        fetchPolicy: "no-cache",
      }),
    ]);

    if (data.data.form_template_by_pk === null) {
      throw new Error("Failed exporting JSON");
    }

    const schema = FormTemplate.fromGQL(
      data.data.form_template_by_pk,
      "lenient"
    );
    const exportContext = Export.getExportContext(
      schema,
      exportContextData.data.grade_config,
      exportContextData.data.school
    );
    const json = Export.exportSchema(schema, exportContext);

    triggerDownload(
      new Blob([JSON.stringify(json, undefined, 2)], {
        type: "text/json",
      }),
      "formTemplate.json"
    );
    setExporting(false);
  };
  return (
    <>
      <Portal containerRef={menuRef}>
        <Flex direction="row" gap={2.5} alignItems="center">
          <ModeMenu
            onPublish={async () => {}}
            mode={GQL.form_template_mode_enum.Published}
            isDisabled={true}
          />
          {flags["form-builder-draft-mode"].enabled ? (
            <Text fontSize="sm" color="gray.500">
              Form version: {formTemplateVersion}
            </Text>
          ) : null}
        </Flex>
      </Portal>
      <Flex direction="column" gap={4} paddingY="2">
        <WithRequiredHasuraRoles
          roles={[HasuraRole.ADMIN, HasuraRole.ORG_ADMIN]}
        >
          {isFormBuilderFullEdit && (
            <Flex justifyContent="right" gap={4}>
              <Button
                isLoading={exporting}
                leftIcon={<RiDownloadLine />}
                onClick={exportFormTemplateJSON}
              >
                Export JSON
              </Button>
            </Flex>
          )}
        </WithRequiredHasuraRoles>

        <WithRequiredHasuraRoles
          roles={[HasuraRole.ADMIN, HasuraRole.ORG_ADMIN]}
        >
          {(!sortedSections || sortedSections?.size === 0) &&
            isFormBuilderFullEdit && (
              <AddSectionButton
                applicableSectionTypes={ALL_SECTION_TYPES}
                newOrder={0}
                leftIcon={<RiAddFill />}
                width="100%"
                height="5rem"
              />
            )}
        </WithRequiredHasuraRoles>

        {sortedSections?.map((section, index) => {
          return (
            <FormTemplateSection
              key={index}
              sectionId={section}
              organizationId={organizationId}
              gradesConfig={gradesConfig}
              order={index}
            />
          );
        })}
      </Flex>
    </>
  );
};
